import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4a46bb01-e425-48c8-a790-c185a7fd0b57/artifacts/6wdpibd5_logomedusa.jpeg';

// Social media icons as SVG
const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Footer = () => {
  const { isDarkMode } = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <footer className={`${isDarkMode ? 'bg-zinc-950' : 'bg-[#0a0a0a]'} text-white py-16 md:py-20`} data-testid="footer">
      <div className="container-custom">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="bg-black p-1 rounded">
                <img 
                  src={LOGO_URL} 
                  alt="MALABISthestic" 
                  className="w-12 h-12 object-contain"
                  style={{ filter: 'invert(1)' }}
                />
              </div>
              <span 
                className="text-xl tracking-widest text-white"
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                MALABIS<span style={{ color: '#D4AF37' }}>theistic</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Premium uniforms for hospitality and healthcare professionals. 
              Elegance meets functionality in every stitch.
            </p>
            <div className="gold-line mb-6" />
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://x.com/theisticmalabis?s=11"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
                data-testid="footer-twitter"
                aria-label="Twitter"
              >
                <TwitterIcon />
              </a>
              <a
                href="https://instagram.com/malabisthestic"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://wa.me/916398130510"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-[#25D366] hover:border-[#25D366] transition-all"
                data-testid="footer-whatsapp"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-white mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Products', path: '/products' },
                { name: 'Catalogue', path: '/catalogue' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-white mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Categories
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Hotel Staff', path: '/products?category=hotel' },
                { name: 'Hospital Staff', path: '/products?category=hospital' },
                { name: 'Chef Uniforms', path: '/products?category=hotel&sub=chef' },
                { name: 'Medical Scrubs', path: '/products?category=hospital&sub=nurses' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                    data-testid={`footer-cat-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 
              className="text-sm uppercase tracking-widest text-white mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-400">
                  Dehradun, Uttarakhand<br />India
                </span>
              </li>
              <li>
                <a 
                  href="tel:+916398130510" 
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={18} className="text-[#D4AF37]" />
                  +91 6398130510
                </a>
              </li>
              <li>
                <a 
                  href="tel:+917037738809" 
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-phone-2"
                >
                  <Phone size={18} className="text-[#D4AF37]" />
                  +91 7037738809
                </a>
              </li>
              <li>
                <a 
                  href="mailto:theisticmalabis@gmail.com" 
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={18} className="text-[#D4AF37]" />
                  theisticmalabis@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            © {currentYear} MALABIStheistic. All rights reserved.
          </p>
          <p className="text-xs text-gray-600">
            Crafted with excellence in Dehradun, India
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
